import { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { MenuItem } from '../types';
import { useAuth } from './useAuth';
import { cache } from '../lib/cache';

const MAX_RETRIES = 5;
const RETRY_DELAY = 2000;

interface MenuContextType {
  menu: MenuItem[];
  loading: boolean;
  error: any;
  refreshMenu: () => Promise<void>;
  realtimeStatus: 'connecting' | 'connected' | 'error' | 'disabled';
}

const MenuContext = createContext<MenuContextType | undefined>(undefined);

export const MenuProvider = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading, isProcessing: authProcessing } = useAuth();
  const [dbMenu, setDbMenu] = useState<MenuItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  // Usiamo ref per tutto ciò che non deve causare re-render o re-esecuzione di useEffect
  const retryCountRef = useRef(0);
  const channelRef = useRef<any>(null);
  const userIdRef = useRef<string | null>(null);
  const pgrstErrorLoggedRef = useRef(false);
  const isSubscribedRef = useRef(false);
  const realtimeRetryCountRef = useRef(0);
  const isRealtimePermanentlyDisabledRef = useRef(false);
  const [realtimeStatus, setRealtimeStatus] = useState<'connecting' | 'connected' | 'error' | 'disabled'>('connecting');

  const fetchMenu = useCallback(async (isRetry = false, invalidateCache = false) => {
    if (!userIdRef.current) return;

    if (!isRetry) {
      retryCountRef.current = 0;
    }

    if (!invalidateCache && !isRetry) {
      const cachedMenu = cache.get<MenuItem[]>('menu_data');
      if (cachedMenu) {
        setDbMenu(cachedMenu);
        setLoading(false);
        return;
      }
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('cpgev_config')
        .select('data')
        .eq('id', 'menu')
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          console.warn('useMenu: Riga "menu" non trovata.');
          setDbMenu([]);
          setLoading(false);
          return;
        }
        throw fetchError;
      }

      if (data?.data && (data.data as any).items) {
        const newItems = (data.data as any).items;
        cache.set('menu_data', newItems);
        
        setDbMenu(prev => {
          if (JSON.stringify(prev) === JSON.stringify(newItems)) return prev;
          return newItems;
        });
      } else {
        setDbMenu([]);
      }

      setError(null);
      pgrstErrorLoggedRef.current = false;
      retryCountRef.current = 0;
      setLoading(false);
    } catch (err: any) {
      const isPgrstError = err?.code === 'PGRST002';
      
      if (isPgrstError && !pgrstErrorLoggedRef.current) {
        console.warn('useMenu: Errore PGRST002 (schema cache). Tentativo di ricaricamento automatico...');
        pgrstErrorLoggedRef.current = true;
      }

      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        const delay = RETRY_DELAY * Math.pow(1.5, retryCountRef.current - 1);
        setTimeout(() => fetchMenu(true, invalidateCache), delay);
      } else {
        console.error('useMenu: Massimo tentativi raggiunto o errore non recuperabile:', err);
        setError(err);
        setLoading(false);
      }
    }
  }, []); // <-- dipendenze vuote: usa solo ref, mai ricreato

  const refreshMenu = useCallback(async () => {
    setLoading(true);
    retryCountRef.current = 0;
    await fetchMenu(false, true);
  }, [fetchMenu]);

  // Modifica l'Effect 1 (fetch iniziale):
  useEffect(() => {
    const isAuthLoading = authLoading || authProcessing;

    if (!isAuthLoading && user?.id) {
      // Evita fetch doppi se user?.id non è davvero cambiato
      if (userIdRef.current === user.id && dbMenu.length > 0) {
        return;
      }
      
      userIdRef.current = user.id;
      setLoading(true);
      retryCountRef.current = 0;
      fetchMenu();
    } else if (!isAuthLoading && !user) {
      setLoading(false);
      setDbMenu([]);
      userIdRef.current = null;
    }
  }, [authLoading, authProcessing, user?.id, fetchMenu, dbMenu.length]);

  const menu = useMemo(() => {
    if (!user) return [];

    const ioNode: MenuItem = {
      item: 'Io',
      route: '/user',
      iconPath: user?.avatar_url,
      icon: !user?.avatar_url ? 'User' : undefined,
      children: [
        { item: 'Profilo', route: '/user/profilo', icon: 'UserCircle' },
        { item: 'Ruoli', route: '/user/ruoli', icon: 'ShieldCheck' },
        { item: 'Mia Dashboard', route: '/user/dashboard', icon: 'LayoutDashboard' },
        { item: 'Google Workspace', route: '/user/workspace', icon: 'Cloud' },
        { item: 'Versione', route: '/user/versione', icon: 'Info' },
        { item: 'Preferenze', route: '/user/preferenze', icon: 'Settings' },
        { item: 'Uscita', route: '/logout', icon: 'LogOut' },
      ]
    };

    return [ioNode, ...dbMenu];
  }, [user, dbMenu]);

  // Sottoscrizione Realtime per il menù
  useEffect(() => {
    if (authLoading || !user?.id) return;
    if (isSubscribedRef.current) return;
    isSubscribedRef.current = true;

    const setupSubscription = async () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('useMenu: Errore getSession in setupSubscription:', error);
        isSubscribedRef.current = false;
        return;
      }
      if (!session) {
        isSubscribedRef.current = false;
        return;
      }

      const realtimeObj = (supabase as any).realtime;
      const rtUrl = realtimeObj?.endPoint || realtimeObj?.url || realtimeObj?.socket?.endPoint || 'URL NON TROVATO';
      const anonKeySnippet = import.meta.env.VITE_SUPABASE_ANON_KEY?.substring(0, 10) + '...';
      
      if (window.location.protocol === 'https:' && rtUrl.startsWith('ws:')) {
        console.error('useMenu: ERRORE PROTOCOLLO - Mixed Content rilevato. Il browser blocca ws:// su siti https://. Assicurati che VITE_SUPABASE_URL usi https://');
      }
      
      const channelName = `menu-changes-${Math.random().toString(36).substring(2, 9)}`;
      channelRef.current = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: '*', // Cattura INSERT, UPDATE e DELETE
            schema: 'public',
            table: 'cpgev_config',
            filter: 'id=eq.menu'
          },
          (payload) => {
            fetchMenu(true);
          }
        )
        .subscribe((status, err) => {
          
          if (err) {
            console.error('useMenu: Dettaglio tecnico errore Realtime:', {
              message: err.message,
              details: err
            });
          }
          
          if (status === 'SUBSCRIBED') {
            setRealtimeStatus('connected');
            realtimeRetryCountRef.current = 0;
          }
          
          if (status === 'CHANNEL_ERROR') {
            const errDetails = err ? (err.message || JSON.stringify(err)) : '';
            console.warn('useMenu: Errore del canale Realtime (replica probabilmente non abilitata in DB). Disabilito realtime.', errDetails);
            setRealtimeStatus('disabled');
            isSubscribedRef.current = true; // Latch
            isRealtimePermanentlyDisabledRef.current = true;
            return;
          }
          
          if (status === 'CLOSED') {
            isSubscribedRef.current = false;
            
            if (realtimeRetryCountRef.current < 5) {
              realtimeRetryCountRef.current++;
              setRealtimeStatus('connecting');
              const delay = Math.min(30000, 2000 * Math.pow(2, realtimeRetryCountRef.current));
              console.warn(`useMenu: Tentativo di riconnessione Realtime ${realtimeRetryCountRef.current}/5 tra ${delay/1000}s...`);
              setTimeout(() => {
                if (user?.id) {
                  isSubscribedRef.current = false;
                  setupSubscription();
                }
              }, delay);
            } else {
              if (realtimeStatus !== 'disabled') {
                setRealtimeStatus('disabled');
              }
            }
          }
        });
    };

    setupSubscription();

    // --- WATCHDOG: Keep-alive ogni 30 secondi ---
    const watchdogInterval = setInterval(() => {
      if (isRealtimePermanentlyDisabledRef.current) return;
      
      const currentStatus = channelRef.current?.state;
      
      if (!currentStatus || currentStatus !== 'joined') {
        console.warn('useMenu: Watchdog ha rilevato connessione inattiva. Forzo ri-sottoscrizione...');
        isSubscribedRef.current = false;
        setupSubscription();
      }
    }, 30000);

    return () => {
      clearInterval(watchdogInterval);
      isSubscribedRef.current = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, authLoading]);

  return (
    <MenuContext.Provider value={{ menu, loading, error, refreshMenu, realtimeStatus }}>
      {children}
    </MenuContext.Provider>
  );
};

export const useMenu = () => {
  const context = useContext(MenuContext);
  if (context === undefined) throw new Error('useMenu must be used within a MenuProvider');
  return context;
};